import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchPendingModeration,
  MODERATION_PAGE_SIZE,
  moderationAction,
  moderationKeys,
  type ModerationItem,
} from '../api';
import { taxonomyKeys } from '@/features/taxonomy/api';

/**
 * `activo` existe por la barra lateral, que enseña la cola pendiente y está
 * montada en **todas** las pantallas. Sin él, cada usuario pediría la cola en
 * cada página: la RLS le devolvería cero filas —no ve nada que no le
 * corresponda—, pero la petición sale igual. Quien no modera no la lanza.
 */
export function usePendingModeration(limit: number = MODERATION_PAGE_SIZE, activo = true) {
  return useQuery({
    // El tamaño entra en la key: pedir «20 más» es otra consulta, no la misma.
    queryKey: [...moderationKeys.pending(), limit],
    queryFn: () => fetchPendingModeration(limit),
    enabled: activo,
    staleTime: 15_000,
    // Mantener lo ya visible mientras llega el tramo siguiente, para que la
    // lista no parpadee al pulsar «Ver más».
    placeholderData: (prev) => prev,
  });
}

export function useModerationAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: moderationAction,
    onSuccess: (_res, input) => {
      // Se quita **esa** fila de lo que ya está en caché, en vez de invalidar y
      // volver a pedirlo todo. Invalidar aquí significaba, tras cada decisión,
      // repetir la consulta de la cola **y** una de parecidos por cada fila que
      // volviera: despachar veinte pendientes eran veinte recargas completas, y
      // entre medias la lista se vaciaba y se repintaba entera.
      //
      // Se recorren todas las variantes de la key porque el tamaño pedido entra
      // en ella: con «Ver 20 más» hay en caché la de 20 y la de 40, y quitar la
      // fila sólo de una dejaría a la otra enseñando algo ya decidido.
      qc.setQueriesData<{ items: ModerationItem[]; total: number }>(
        { queryKey: moderationKeys.pending() },
        (prev) =>
          prev
            ? {
                items: prev.items.filter((i) => i.id !== input.queue_id),
                // El total baja con ella: es lo que alimenta «Ver N más» y los
                // dos contadores de la aplicación.
                total: Math.max(0, prev.total - 1),
              }
            : prev,
      );
      void qc.invalidateQueries({ queryKey: taxonomyKeys.all });
    },
  });
}
